import type { FC, ReactNode } from 'react'
import type { LoginRole, ModuleId, Viewer } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { initials } from '@/lib/format'
import { BrandLockup } from '@/components/generals/logo'
import { MODULE_ICONS } from '@/components/landing/module-icons'

import {
    ArrowRight,
    ChevronDown,
    GraduationCap,
    LayoutDashboard,
    Menu,
    Plus,
    ShieldCheck,
    Users,
    X
} from 'lucide-react'

import { MODULES } from '@/constants/campus'

interface NavbarProps {
    viewer: Viewer | null
    onSelectModule: (id: ModuleId) => void
    onOpenLogin: (role: LoginRole, signUp?: boolean) => void
}

const SIGN_IN_ROLES: Array<{ role: LoginRole, label: string, description: string, icon: typeof GraduationCap }> = [
    { role: 'st', label: "Student", description: "Sign in or create your B.Tech account", icon: GraduationCap },
    { role: 'fp', label: "Coordinator", description: "Future Pioneers appointed by the T&P Cell", icon: Users },
    { role: 'tp', label: "T&P Officer", description: "Verify passes and run campus placements", icon: ShieldCheck }
]

const NAV_LINKS: Array<{ label: string, module?: ModuleId, href?: string }> = [
    { label: "32-Month Journey", module: 'journey' },
    { label: "Year-Wise Pricing", href: '#pricing' },
    { label: "Study Vault", module: 'study-vault' },
    { label: "Placements", module: 'placements' }
]

// Closes a popover on an outside click or Escape.
const useDismiss = <T extends HTMLElement>(open: boolean, onClose: () => void) => {
    const ref = useRef<T>(null)

    useEffect(() => {
        if (!open) return

        const onPointer = (event: PointerEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) onClose()
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.addEventListener('pointerdown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            document.removeEventListener('pointerdown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    return ref
}

const popover = {
    initial: { opacity: 0, y: 8, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 6, scale: 0.98 },
    transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] as const }
}

export const Navbar: FC<NavbarProps> = ({ viewer, onSelectModule, onOpenLogin }): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [modulesOpen, setModulesOpen] = useState(false)
    const [signInOpen, setSignInOpen] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)

    const modulesRef = useDismiss<HTMLLIElement>(modulesOpen, () => setModulesOpen(false))
    const signInRef = useDismiss<HTMLDivElement>(signInOpen, () => setSignInOpen(false))

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const pickModule = (id: ModuleId) => {
        setModulesOpen(false)
        setMobileOpen(false)
        onSelectModule(id)
    }

    const viewerName = viewer?.kind === 'student' ? viewer.student.name : viewer?.kind === 'staff' ? viewer.staff.name : ''

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300',
                scrolled || mobileOpen
                    ? 'border-white/10 bg-midnight/80 shadow-[0_18px_50px_-24px_rgba(2,6,23,0.85)] backdrop-blur-xl'
                    : 'border-white/[0.06] bg-midnight/40 backdrop-blur-md'
            )}
        >
            <nav aria-label='Main' className='mx-auto flex h-[72px] max-w-7xl items-center gap-4 px-4 sm:px-6 xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]'>
                <Link href='/' aria-label='GKV Tech Campus home' className='min-w-0 justify-self-start rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-indigo-400'>
                    <BrandLockup size={42} />
                </Link>

                <ul className='hidden items-center gap-1 xl:flex'>
                    <li ref={modulesRef} className='relative'>
                        <button
                            type='button'
                            aria-expanded={modulesOpen}
                            aria-haspopup='true'
                            onClick={() => setModulesOpen(value => !value)}
                            className='inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/[0.06] hover:text-white'
                        >
                            Platform Modules

                            <Plus className={cn('size-3.5 transition-transform duration-300', modulesOpen && 'rotate-45')} />
                        </button>

                        <AnimatePresence>
                            {modulesOpen && (
                                <motion.div
                                    {...popover}
                                    className='absolute left-1/2 top-full mt-3 w-[440px] -translate-x-1/2 rounded-2xl border border-white/10 bg-midnight-card/95 p-2 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl'
                                >
                                    {MODULES.map(module => {
                                        const Icon = MODULE_ICONS[module.id]

                                        return (
                                            <button
                                                key={module.id}
                                                type='button'
                                                onClick={() => pickModule(module.id)}
                                                className='group flex w-full items-start gap-3 rounded-xl p-3 text-left transition hover:bg-white/[0.06]'
                                            >
                                                <span className='grid size-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-indigo-300 transition group-hover:border-indigo-400/40 group-hover:text-white'>
                                                    <Icon className='size-4' />
                                                </span>

                                                <span>
                                                    <span className='block text-sm font-semibold text-white'>
                                                        {module.label}
                                                    </span>

                                                    <span className='mt-0.5 block text-xs leading-relaxed text-slate-400'>
                                                        {module.headline}
                                                    </span>
                                                </span>
                                            </button>
                                        )
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </li>

                    {NAV_LINKS.map(link => (
                        <li key={link.label}>
                            {link.module ? (
                                <button
                                    type='button'
                                    onClick={() => pickModule(link.module as ModuleId)}
                                    className='rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/[0.06] hover:text-white'
                                >
                                    {link.label}
                                </button>
                            ) : (
                                <a href={link.href} className='block rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/[0.06] hover:text-white'>
                                    {link.label}
                                </a>
                            )}
                        </li>
                    ))}
                </ul>

                <div className='ml-auto flex shrink-0 items-center justify-end gap-2 xl:col-start-3 xl:ml-0'>
                    {viewer ? (
                        <Link
                            href='/portal'
                            className='hidden items-center gap-2 rounded-lg border border-white/20 py-1.5 pl-1.5 pr-3.5 text-sm font-semibold text-white transition hover:bg-white/10 sm:inline-flex'
                        >
                            <span className='grid size-7 place-items-center rounded-md bg-gradient-to-br from-indigo-500 to-emerald-500 text-[11px] font-bold'>
                                {initials(viewerName)}
                            </span>

                            Open portal
                        </Link>
                    ) : (
                        <div ref={signInRef} className='relative hidden sm:block'>
                            <button
                                type='button'
                                aria-expanded={signInOpen}
                                aria-haspopup='true'
                                onClick={() => setSignInOpen(value => !value)}
                                className='inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10'
                            >
                                Sign In

                                <ChevronDown className={cn('size-4 transition-transform duration-200', signInOpen && 'rotate-180')} />
                            </button>

                            <AnimatePresence>
                                {signInOpen && (
                                    <motion.div
                                        {...popover}
                                        className='absolute right-0 top-full mt-3 w-[300px] rounded-2xl border border-white/10 bg-midnight-card/95 p-2 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl'
                                    >
                                        <p className='px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                                            Sign in as
                                        </p>

                                        {SIGN_IN_ROLES.map(item => (
                                            <button
                                                key={item.role}
                                                type='button'
                                                onClick={() => {
                                                    setSignInOpen(false)
                                                    onOpenLogin(item.role)
                                                }}
                                                className='group flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-white/[0.06]'
                                            >
                                                <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-slate-200 transition group-hover:bg-indigo-500/20 group-hover:text-white'>
                                                    <item.icon className='size-4' />
                                                </span>

                                                <span className='min-w-0'>
                                                    <span className='block text-sm font-semibold text-white'>
                                                        {item.label}
                                                    </span>

                                                    <span className='block text-xs text-slate-400'>
                                                        {item.description}
                                                    </span>
                                                </span>
                                            </button>
                                        ))}

                                        <div className='mt-1 border-t border-white/10 px-3 pb-2 pt-3 text-xs text-slate-400'>
                                            New student?{' '}

                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setSignInOpen(false)
                                                    onOpenLogin('st', true)
                                                }}
                                                className='font-semibold text-white underline-offset-4 hover:underline'
                                            >
                                                Create your account
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                    <a
                        href='#pricing'
                        className='group relative hidden items-center gap-2 overflow-hidden rounded-lg bg-crimson px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(217,45,32,0.9)] transition hover:bg-crimson-dark sm:inline-flex'
                    >
                        Get Started

                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setMobileOpen(value => !value)}
                        aria-expanded={mobileOpen}
                        aria-controls='mobile-menu'
                        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                        className='grid size-10 shrink-0 place-items-center rounded-lg border border-white/15 text-white transition hover:bg-white/10 xl:hidden'
                    >
                        {mobileOpen ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        id='mobile-menu'
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className='overflow-hidden border-t border-white/10 xl:hidden'
                    >
                        <div className='mx-auto max-h-[calc(100dvh-72px)] max-w-7xl space-y-5 overflow-y-auto px-4 pb-6 pt-4 sm:px-6'>
                            <div>
                                <p className='mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                                    Platform modules
                                </p>

                                <div className='grid grid-cols-2 gap-2'>
                                    {MODULES.map(module => {
                                        const Icon = MODULE_ICONS[module.id]

                                        return (
                                            <button
                                                key={module.id}
                                                type='button'
                                                onClick={() => pickModule(module.id)}
                                                className='flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left text-sm font-medium text-slate-200'
                                            >
                                                <Icon className='size-4 shrink-0 text-indigo-300' />

                                                {module.label}
                                            </button>
                                        )
                                    })}

                                    <a
                                        href='#pricing'
                                        onClick={() => setMobileOpen(false)}
                                        className='flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm font-medium text-slate-200'
                                    >
                                        <LayoutDashboard className='size-4 shrink-0 text-indigo-300' />

                                        Year-Wise Pricing
                                    </a>
                                </div>
                            </div>

                            {viewer ? (
                                <Link href='/portal' className='flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 py-3 text-sm font-semibold text-white'>
                                    Open your portal

                                    <ArrowRight className='size-4' />
                                </Link>
                            ) : (
                                <div>
                                    <p className='mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>
                                        Sign in as
                                    </p>

                                    <div className='grid grid-cols-3 gap-2'>
                                        {SIGN_IN_ROLES.map(item => (
                                            <button
                                                key={item.role}
                                                type='button'
                                                onClick={() => {
                                                    setMobileOpen(false)
                                                    onOpenLogin(item.role)
                                                }}
                                                className='flex flex-col items-center gap-1.5 rounded-xl border border-white/15 px-2 py-3 text-xs font-semibold text-white'
                                            >
                                                <item.icon className='size-4' />

                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <a
                                href='#pricing'
                                onClick={() => setMobileOpen(false)}
                                className='flex w-full items-center justify-center gap-2 rounded-xl bg-crimson py-3 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(217,45,32,0.9)]'
                            >
                                Get Started · Join the Pass

                                <ArrowRight className='size-4' />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    )
}
